// ═══════════════════════════════════════════════════════════════════════
// Dispositifs sociaux — onglet « Prêts » d'une organisation : demandes paginées (filtre d'état, de produit), saisie
// d'une demande au guichet pour un salarié (jamais pour soi : refus ACTE_SUR_SOI_INTERDIT du serveur), avec le
// récapitulatif calculé par le serveur avant l'enregistrement.
// Routes : GET /api/dispositifs/prets/demandes, POST /simulation, POST /demandes (Idempotency-Key).
// ═══════════════════════════════════════════════════════════════════════
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Calculator, HandCoins, Plus } from 'lucide-react';
import { Bouton, BandeauInfo } from '../../design/composants';
import { useDs, toArray } from './api';
import { Champ, Etat, PaginationDs } from './commun';
import { ChoixPersonne } from './FenetresMandat';
import { useSocial, francs, dateLongue, lireMontant, ETATS_PRET, FILTRES_PRET } from './apiSocial';
import { useLectureSociale, EtatsLecture, FiltreEtats, FenetreActe, MarqueDemo, erreurSociale } from './communSocial';
import { LigneSuivi } from '../../components/suivi/SuiviDemande'; // prochain acteur : `suivi` des lignes (lecture)

/** Produits de l'organisation d'un type donné (dispositifs actifs). */
export function useProduits(organisationId, type) {
  const ds = useDs();
  const l = useLectureSociale(() => ds.dispositifs(organisationId, { page: 1, taille: 100 }), [ds, organisationId]);
  return { ...l, produits: toArray(l.donnees).filter((d) => d.type === type) };
}

function Recapitulatif({ recap }) {
  if (!recap) return null;
  const r = recap.recapitulatif || {};
  return (
    <div className="rh-carte" style={{ margin: '0 0 12px', padding: '10px 14px', background: 'var(--sg-fond)' }}>
      {r.phrase && <p style={{ margin: '0 0 6px', fontWeight: 600 }}>{r.phrase}</p>}
      <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: '4px 12px', fontSize: 14 }}>
        {toArray(r.lignes).map((l) => (
          <div key={l.libelle} style={{ display: 'contents' }}>
            <dt style={{ color: 'var(--sg-texte-2)' }}>{l.libelle}</dt><dd style={{ margin: 0, fontWeight: l.fort ? 600 : 400, textAlign: 'right' }}>{l.valeur}</dd>
          </div>
        ))}
      </dl>
      {toArray(r.avertissements).map((a) => <p key={a} className="rh-form-aide" style={{ margin: '6px 0 0' }}>{a}</p>)}
    </div>
  );
}

export function FenetreGuichetPret({ organisationId, produits, onFermer, onCree }) {
  const api = useSocial();
  const [produit, setProduit] = useState(produits.length === 1 ? produits[0].id : '');
  const [personne, setPersonne] = useState(null);
  const [montant, setMontant] = useState('');
  const [duree, setDuree] = useState('');
  const [objet, setObjet] = useState('');
  const [accord, setAccord] = useState(false);
  const [recap, setRecap] = useState(null);
  const [erreurSim, setErreurSim] = useState(null);
  const m = lireMontant(montant);
  const n = /^\d{1,3}$/.test(duree) ? Number(duree) : null;
  const saisieOk = produit && personne && m && n;
  const simuler = async () => {
    setErreurSim(null); setRecap(null);
    try { setRecap(await api.simulerPret({ dispositif_id: produit, montant: m, nb_echeances: n })); } catch (e) { setErreurSim(erreurSociale(e)); }
  };
  const changer = (f) => (e) => { f(e.target.value); setRecap(null); setAccord(false); };
  return (
    <FenetreActe titre="Demande de prêt au guichet" Icone={HandCoins} libelleBouton="Enregistrer la demande" pret={!!(saisieOk && recap && accord)}
      aide="Saisie pour un salarié qui se présente au guichet. Vous ne pouvez pas saisir votre propre demande : passez par votre espace salarié."
      onFermer={onFermer}
      onConfirmer={async (cle) => {
        const r = await api.saisirPret({ dispositif_id: produit, employe_id: personne.id, montant: m, nb_echeances: n, accord: true, ...(objet.trim() ? { objet_libre: objet.trim() } : {}) }, cle);
        onCree(r.demande);
      }}>
      <Champ libelle="Produit de prêt" requis>
        <select className="rh-champ-saisie" value={produit} onChange={changer(setProduit)}>
          <option value="">Choisir…</option>
          {produits.map((p) => <option key={p.id} value={p.id} disabled={p.etat !== 'ACTIF'}>{p.libelle}{p.etat !== 'ACTIF' ? ' (non actif)' : ''}</option>)}
        </select>
      </Champ>
      <ChoixPersonne choisie={personne} onChoisir={(p) => { setPersonne(p); setRecap(null); setAccord(false); }} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
        <Champ libelle="Montant demandé (F)" requis><input className="rh-champ-saisie" inputMode="numeric" value={montant} onChange={changer(setMontant)} placeholder="300 000" /></Champ>
        <Champ libelle="Durée (mois)" requis><input className="rh-champ-saisie" inputMode="numeric" value={duree} onChange={changer(setDuree)} placeholder="10" /></Champ>
      </div>
      <Champ libelle="Objet (facultatif)" aide="Information libre, 300 caractères au plus.">
        <input className="rh-champ-saisie" maxLength={300} value={objet} onChange={(e) => setObjet(e.target.value)} />
      </Champ>
      <div style={{ marginBottom: 12 }}>
        <Bouton Icone={Calculator} disabled={!saisieOk} onClick={simuler}>Calculer le récapitulatif</Bouton>
      </div>
      {erreurSim && <BandeauInfo ton="alerte" titre="Demande impossible en l'état">{erreurSim.message}{erreurSim.erreurs.length > 0 && ` (${erreurSim.erreurs.join(' ; ')})`}</BandeauInfo>}
      <Recapitulatif recap={recap} />
      {recap && (
        <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 14, marginBottom: 12 }}>
          <input type="checkbox" checked={accord} onChange={(e) => setAccord(e.target.checked)} style={{ marginTop: 3 }} />
          <span>Le salarié a pris connaissance de ce récapitulatif et donne son accord. Le montant et la durée définitifs seront fixés par la décision.</span>
        </label>
      )}
    </FenetreActe>
  );
}

export default function PretsSection({ organisation, droits }) {
  const api = useSocial();
  const naviguer = useNavigate();
  const [statut, setStatut] = useState('');
  const [dispositif, setDispositif] = useState('');
  const [page, setPage] = useState(1);
  const [guichet, setGuichet] = useState(false);
  const { produits } = useProduits(organisation.id, 'PRET');
  const liste = useLectureSociale(() => api.prets(organisation.id, { page, taille: 25, statut, dispositif }), [api, organisation.id, page, statut, dispositif]);
  const elements = toArray(liste.donnees);
  const ouvrir = (d) => naviguer(`/dispositifs/${organisation.id}/prets/${d.id}`);
  const peutSaisir = droits.a('ds_pret_saisir_guichet') && organisation.etat === 'ACTIVE';
  return (
    <>
      <div className="rh-outils" style={{ justifyContent: 'space-between' }}>
        <FiltreEtats table={ETATS_PRET} codes={FILTRES_PRET} valeur={statut} onChange={(c) => { setStatut(c); setPage(1); }} />
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {produits.length > 1 && (
            <select className="rh-champ-saisie" aria-label="Produit" value={dispositif} onChange={(e) => { setDispositif(e.target.value); setPage(1); }}>
              <option value="">Tous les produits</option>
              {produits.map((p) => <option key={p.id} value={p.id}>{p.libelle}</option>)}
            </select>
          )}
          {peutSaisir && <Bouton variante="primaire" Icone={Plus} disabled={!produits.length} onClick={() => setGuichet(true)}>Saisir au guichet</Bouton>}
        </div>
      </div>
      <section className="rh-carte" style={{ padding: 0 }}>
        <EtatsLecture lecture={liste} Icone={HandCoins} estVide={!elements.length}
          titreVide={statut ? 'Aucune demande dans cet état' : 'Aucune demande de prêt'}
          vide={statut ? 'Choisissez un autre état.' : produits.length
            ? 'Les demandes déposées par les salariés (application ou guichet) apparaissent ici, de l\'instruction jusqu\'au remboursement.'
            : 'Aucun produit de prêt n\'est encore préparé dans cette organisation : créez-le dans l\'onglet Dispositifs.'}>
          <div className="rh-conteneur-tableau">
            <table className="rh-tableau">
              <thead><tr><th>Demande</th><th>Produit</th><th style={{ textAlign: 'right' }}>Montant</th><th>État</th><th aria-label="Ouvrir" /></tr></thead>
              <tbody>
                {elements.map((d) => (
                  <tr key={d.id} className="rh-ligne" tabIndex={0} onClick={() => ouvrir(d)} onKeyDown={(e) => { if (e.key === 'Enter') ouvrir(d); }}>
                    <td>
                      <span className="rh-cellule-principale">{d.salarie}</span>
                      <span className="rh-cellule-secondaire" style={{ display: 'block' }}>{d.numero} · {d.matricule}{d.depose_le ? ` · déposée le ${dateLongue(d.depose_le)}` : ''} <MarqueDemo actif={d.donnees_demo} /></span>
                    </td>
                    <td>{d.dispositif}</td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                      {francs(d.montant_accorde ?? d.montant_demande)}
                      <span className="rh-cellule-secondaire" style={{ display: 'block' }}>{d.montant_accorde !== null && d.montant_accorde !== undefined ? 'accordé' : 'demandé'} · {d.nb_echeances_accorde || d.nb_echeances_demande} mois</span>
                    </td>
                    <td><Etat table={ETATS_PRET} code={d.statut} /><LigneSuivi suivi={d.suivi} /></td>
                    <td style={{ width: 32, color: 'var(--sg-texte-4)' }}><ChevronRight size={16} aria-hidden="true" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <PaginationDs donnees={liste.donnees} onPage={setPage} />
        </EtatsLecture>
      </section>
      {guichet && <FenetreGuichetPret organisationId={organisation.id} produits={produits} onFermer={() => setGuichet(false)} onCree={(d) => { setGuichet(false); ouvrir(d); }} />}
    </>
  );
}
